import { useState } from 'react'
import { EventConfig, GallerySettings as GallerySettingsType } from '@/types'
import { Switch } from '@/admin/components/Switch'
import { Monitor, RefreshCw, Layout, Clock, Download, Eye, Sparkles, Video } from 'lucide-react'
import { generateShareToken, calculateExpiration } from '@/lib/utils'

interface GallerySettingsProps {
  event: EventConfig
  onChange: (updates: Partial<EventConfig>) => void
}

export default function GallerySettings({ event, onChange }: GallerySettingsProps) {
  const [copied, setCopied] = useState(false)

  const gallery: GallerySettingsType = event.gallerySettings || {
    enabled: true,
    publicAccess: 'disabled',
    publicAccessDuration: 'permanent',
    publicAccessDurationValue: 1,
    publicAccessExpiresAt: null,
    publicAccessCreatedAt: null,
    publicToken: null,
    allowDownloads: true,
    allowZipDownload: true,
    showSurveyResults: false,
    showCaptureTime: true,
    galleryTheme: 'dark',
    gridColumns: 3,
    watermarkPreview: false,
    sortBy: 'newestFirst',
  }

  const update = (updates: Partial<GallerySettingsType>) => {
    onChange({ gallerySettings: { ...gallery, ...updates } })
  }

  return (
    <div className="max-w-3xl space-y-8 pb-12">
      <div>
        <h2 className="text-xl font-bold">Gallery Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">Configure visibility, access control, and layout for the guest photo gallery.</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {/* Gallery Status */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <Monitor className="h-5 w-5" />
              </div>
              <span className="font-semibold">Online Gallery Status</span>
            </div>
            <Switch
              checked={gallery.enabled}
              onCheckedChange={(enabled: boolean) => update({ enabled })}
            />
          </div>
          <p className="text-xs text-muted-foreground">Toggle the online gallery for this event entirely.</p>
        </div>

        {/* Public Access Visibility */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-400">
                <Eye className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold">Public Gallery Visibility</span>
                <span className="text-xs text-muted-foreground mt-0.5">Control whether the gallery is public or restricted to admin login.</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-muted-foreground">
                {gallery.publicAccess === 'enabled' ? 'Public (Everyone)' : 'Private (Signed-in only)'}
              </span>
              <Switch
                checked={gallery.publicAccess === 'enabled'}
                onCheckedChange={(enabled: boolean) => {
                  const publicAccess = enabled ? 'enabled' : 'disabled';
                  let expiresAt = null;
                  let publicToken = gallery.publicToken;
                  if (enabled) {
                    expiresAt = calculateExpiration(gallery.publicAccessDuration || 'permanent', gallery.publicAccessDurationValue || 1);
                    if (!publicToken) {
                      publicToken = generateShareToken();
                    }
                  }
                  update({ 
                    publicAccess, 
                    publicAccessExpiresAt: expiresAt,
                    publicAccessCreatedAt: enabled ? new Date().toISOString() : null,
                    publicToken
                  });
                }}
              />
            </div>
          </div>
          
          {gallery.publicAccess === 'enabled' && (
            <div className="mt-6 border-t border-border pt-6 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                {/* Expiration Duration Type */}
                <div>
                  <label className="text-sm font-semibold block mb-2">Duration</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'hours', label: 'Hours' },
                      { id: 'days', label: 'Days' },
                      { id: 'permanent', label: 'Permanent' }
                    ].map(opt => (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => {
                          const duration = opt.id as any;
                          const expiresAt = calculateExpiration(duration, gallery.publicAccessDurationValue || 1);
                          update({ 
                            publicAccessDuration: duration,
                            publicAccessExpiresAt: expiresAt
                          });
                        }}
                        className={`rounded-lg py-2 text-xs font-semibold border transition-colors ${gallery.publicAccessDuration === opt.id ? 'bg-primary/15 text-primary border-primary/20' : 'hover:bg-muted border-border bg-card'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Expiration Duration Value */}
                {gallery.publicAccessDuration !== 'permanent' && (
                  <div>
                    <label className="text-sm font-semibold block mb-2">
                      Expire after {gallery.publicAccessDurationValue || 1} {gallery.publicAccessDuration}
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min={1}
                        max={gallery.publicAccessDuration === 'hours' ? 24 : 30}
                        value={gallery.publicAccessDurationValue || 1}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          const expiresAt = calculateExpiration(gallery.publicAccessDuration || 'hours', val);
                          update({ 
                            publicAccessDurationValue: val,
                            publicAccessExpiresAt: expiresAt
                          });
                        }}
                        className="flex-1 accent-primary"
                      />
                      <span className="text-sm font-bold text-primary min-w-[2rem] text-right">
                        {gallery.publicAccessDurationValue || 1}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Live status and link sharing */}
              <div className="rounded-xl bg-muted/40 p-4 border border-border/60 space-y-4">
                <div className="text-xs">
                  <span className="font-bold text-foreground block mb-1">Status:</span>
                  <span className="text-muted-foreground">
                    {gallery.publicAccessExpiresAt ? (
                      <>This gallery is publicly accessible until <strong className="text-primary font-semibold">{new Date(gallery.publicAccessExpiresAt).toLocaleString()}</strong>.</>
                    ) : (
                      <>This gallery is <strong className="text-emerald-400 font-semibold">permanently accessible</strong> publicly.</>
                    )}
                  </span>
                </div>

                {gallery.publicToken && (
                  <div className="space-y-2 pt-2 border-t border-border/60">
                    <span className="text-xs font-bold text-foreground block">Public Shareable Link:</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}/gallery?eventId=${event.id}&token=${gallery.publicToken}`}
                        className="flex-1 bg-card border border-border rounded-lg px-3 py-1.5 text-xs text-muted-foreground font-mono select-all focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const url = `${window.location.origin}/gallery?eventId=${event.id}&token=${gallery.publicToken}`;
                          navigator.clipboard.writeText(url);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border transition-all min-w-[5.5rem] ${copied ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-primary text-white border-transparent hover:bg-primary/90'}`}
                      >
                        {copied ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newToken = generateShareToken();
                          update({ publicToken: newToken });
                        }}
                        title="Regenerate Token"
                        className="rounded-lg p-1.5 border border-border bg-card hover:bg-muted text-muted-foreground transition-colors"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      Note: The standard URL <code className="font-mono text-foreground bg-muted px-1 py-0.5 rounded">/gallery?eventId={event.id}</code> still requires a login. Only guests visiting via this unique link can view it without credentials.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Gallery Downloads Section */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:col-span-2">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground/40 mb-6">Gallery Permissions</h3>
          <div className="grid gap-6 sm:grid-cols-2">
             <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <Download className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">Allow Individual Downloads</span>
                      <span className="text-xs text-muted-foreground">Allow guests to download individual photos.</span>
                   </div>
                </div>
                <Switch
                  checked={gallery.allowDownloads !== false}
                  onCheckedChange={(val: boolean) => update({ allowDownloads: val })}
                />
             </div>

             <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <Download className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">Allow Session ZIP Downloads</span>
                      <span className="text-xs text-muted-foreground">Allow guests to download the entire session as a ZIP file.</span>
                   </div>
                </div>
                <Switch
                  checked={gallery.allowZipDownload !== false}
                  onCheckedChange={(val: boolean) => update({ allowZipDownload: val })}
                />
             </div>

             <div className="flex items-center justify-between border-t border-border/60 pt-4 mt-2 sm:col-span-2">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <Download className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">Allow Guest Bulk (Event) ZIP Download</span>
                      <span className="text-xs text-muted-foreground">Allow guests to download all event photos as a single ZIP file (always enabled for admins).</span>
                   </div>
                </div>
                <Switch
                  checked={gallery.allowBulkDownload === true}
                  onCheckedChange={(val: boolean) => update({ allowBulkDownload: val })}
                />
             </div>

             <div className="flex items-center justify-between border-t border-border/60 pt-4 mt-2 sm:col-span-2">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <Download className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">Include Videos in ZIP (Admin Only)</span>
                      <span className="text-xs text-muted-foreground">Include video captures when downloading ZIP archives as a logged-in admin (guests will still only get photos).</span>
                   </div>
                </div>
                <Switch
                  checked={gallery.includeVideosInZip === true}
                  onCheckedChange={(val: boolean) => update({ includeVideosInZip: val })}
                />
             </div>
          </div>
        </div>

        {/* Display & Layout settings */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:col-span-2">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground/40 mb-6">Layout & Design</h3>
          <div className="grid gap-6 sm:grid-cols-2">
             {/* Grid Columns */}
             <div>
                <label className="text-sm font-semibold block mb-2 flex items-center gap-2">
                  <Layout className="h-4 w-4 text-primary" />
                  Grid Layout Columns
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[2, 3, 4].map(cols => (
                    <button
                      type="button"
                      key={cols}
                      onClick={() => update({ gridColumns: cols as any })}
                      className={`rounded-lg py-2 text-xs font-semibold border transition-colors ${gallery.gridColumns === cols ? 'bg-primary/15 text-primary border-primary/20 font-bold' : 'hover:bg-muted border-border bg-card'}`}
                    >
                      {cols} Columns
                    </button>
                  ))}
                </div>
             </div>

             {/* Sorting */}
             <div>
                <label className="text-sm font-semibold block mb-2 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-primary" />
                  Sort Photos By
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'newestFirst', label: 'Newest First' },
                    { id: 'oldestFirst', label: 'Oldest First' }
                  ].map(opt => (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => update({ sortBy: opt.id as any })}
                      className={`rounded-lg py-2 text-xs font-semibold border transition-colors ${gallery.sortBy === opt.id ? 'bg-primary/15 text-primary border-primary/20 font-bold' : 'hover:bg-muted border-border bg-card'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
             </div>

             {/* Group by Session Switch */}
             <div className="sm:col-span-2 flex items-center justify-between border-t border-border/60 pt-4 mt-2">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <Layout className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">Group by Session</span>
                      <span className="text-xs text-muted-foreground">Group images into session folders rather than displaying all individual images flat.</span>
                   </div>
                </div>
                <Switch
                  checked={gallery.groupBySession !== false}
                  onCheckedChange={(val: boolean) => update({ groupBySession: val })}
                />
             </div>
          </div>
        </div>

        {/* Visibility Extra Options */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:col-span-2">
          <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground/40 mb-6">Additional Info Options</h3>
          <div className="grid gap-6 sm:grid-cols-2">
             <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                      <Clock className="h-5 w-5" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-sm font-semibold">Show Photo Capture Time</span>
                      <span className="text-xs text-muted-foreground">Display exact capture timestamps on photo tiles.</span>
                   </div>
                </div>
                <Switch
                  checked={gallery.showCaptureTime !== false}
                  onCheckedChange={(val: boolean) => update({ showCaptureTime: val })}
                />
             </div>

              <div className="flex items-center justify-between">
                 <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                       <Sparkles className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col">
                       <span className="text-sm font-semibold">Show Survey Responses</span>
                       <span className="text-xs text-muted-foreground">Display survey entries alongside photos in the session.</span>
                    </div>
                 </div>
                 <Switch
                   checked={gallery.showSurveyResults === true}
                   onCheckedChange={(val: boolean) => update({ showSurveyResults: val })}
                 />
              </div>

              <div className="flex items-center justify-between border-t border-border/60 pt-4 mt-2 sm:col-span-2">
                 <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-zinc-500/10 p-2 text-zinc-400">
                       <Video className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col">
                       <span className="text-sm font-semibold">Show Videos in Gallery</span>
                       <span className="text-xs text-muted-foreground">Display captured session videos in the guest gallery (default is NO).</span>
                    </div>
                 </div>
                 <Switch
                   checked={gallery.showVideos === true}
                   onCheckedChange={(val: boolean) => update({ showVideos: val })}
                 />
              </div>
           </div>
        </div>
      </div>
    </div>
  )
}
